
import { useAuth } from "@/context/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useState, useEffect } from "react";
import { UserRound, Users, Mail, Database, Trophy, Flag, Landmark } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { SignatoriesTab } from "./tabs/SignatoriesTab";
import { MembersTab } from "./tabs/MembersTab";
import { NewsletterTab } from "./tabs/NewsletterTab";
import { CRMTab } from "./tabs/CRMTab";
import { ContestTab } from "./tabs/ContestTab";
import { GolfTab } from "./tabs/GolfTab";
import { SenatTab } from "./tabs/SenatTab";

type TabType = "signataires" | "membres" | "newsletter" | "crm" | "concours" | "golf" | "senat" | null;

type CountsType = {
  signatories: number;
  members: number;
  newsletter: number;
  contest: number;
  golf: number;
  senat: number;
};

export default function ReportPage() {
  const { logout, user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>(null);
  const [counts, setCounts] = useState<CountsType>({ signatories: 0, members: 0, newsletter: 0, contest: 0, golf: 0, senat: 0 });

  const fetchCounts = async () => {
    try {
      console.log("Fetching counts...");
      
      const { count: signatoriesCount, error: signatoriesError } = await supabase
        .from('charter_signatories')
        .select('id', { count: 'exact', head: true });

      if (signatoriesError) console.error("Error fetching signatories count:", signatoriesError);

      const { count: membersCount, error: membersError } = await supabase
        .from('memberships')
        .select('id', { count: 'exact', head: true });

      if (membersError) console.error("Error fetching members count:", membersError);

      const { data: newsletterCount, error: newsletterError } = await supabase
        .rpc('get_newsletter_subscribers_count');

      if (newsletterError) console.error("Error fetching newsletter count:", newsletterError);

      const { count: contestCount, error: contestError } = await supabase
        .from('contest_registrations')
        .select('id', { count: 'exact', head: true });

      if (contestError) console.error("Error fetching contest count:", contestError);

      const { count: golfCount, error: golfError } = await supabase
        .from('golf_registrations')
        .select('id', { count: 'exact', head: true });

      if (golfError) console.error("Error fetching golf count:", golfError);

      const { count: senatCount, error: senatError } = await supabase
        .from('senat_dinner_registrations')
        .select('id', { count: 'exact', head: true });

      if (senatError) console.error("Error fetching senat count:", senatError);

      setCounts({
        signatories: signatoriesCount || 0,
        members: membersCount || 0,
        newsletter: newsletterCount || 0,
        contest: contestCount || 0,
        golf: golfCount || 0,
        senat: senatCount || 0,
      });
      
      console.log("Counts fetched:", { signatoriesCount, membersCount, newsletterCount, contestCount, golfCount, senatCount });
    } catch (error) {
      console.error('Error fetching counts:', error);
    }
  };

  useEffect(() => {
    fetchCounts();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#00123A]/5 to-[#FCACF4]/10 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#00123A]">Tableau de bord</h1>
            <p className="text-muted-foreground">Connecté en tant que {user?.email}</p>
          </div>
          <Button onClick={logout} variant="outline">Se déconnecter</Button>
        </div>

        <div className="flex gap-4 mb-8 flex-wrap">
          <Button 
            onClick={() => setActiveTab("signataires")}
            className={`flex-1 h-auto py-6 min-w-[200px] ${activeTab === "signataires" ? "bg-[#00123A]" : "bg-[#00123A]/80"}`}
          >
            <div className="flex flex-col items-center gap-2">
              <UserRound size={32} />
              <span className="text-xl font-semibold">Signataires</span>
              <span className="text-sm font-medium">#{counts.signatories}</span>
            </div>
          </Button>
          
          <Button 
            onClick={() => setActiveTab("membres")}
            className={`flex-1 h-auto py-6 min-w-[200px] ${activeTab === "membres" ? "bg-[#00123A]" : "bg-[#00123A]/80"}`}
          >
            <div className="flex flex-col items-center gap-2">
              <Users size={32} />
              <span className="text-xl font-semibold">Membres</span>
              <span className="text-sm font-medium">#{counts.members}</span>
            </div>
          </Button>

          <Button 
            onClick={() => setActiveTab("newsletter")}
            className={`flex-1 h-auto py-6 min-w-[200px] ${activeTab === "newsletter" ? "bg-[#00123A]" : "bg-[#00123A]/80"}`}
          >
            <div className="flex flex-col items-center gap-2">
              <Mail size={32} />
              <span className="text-xl font-semibold">Abonnés newsletter</span>
              <span className="text-sm font-medium">#{counts.newsletter}</span>
            </div>
          </Button>
          
          <Button 
            onClick={() => setActiveTab("crm")}
            className={`flex-1 h-auto py-6 min-w-[200px] ${activeTab === "crm" ? "bg-[#00123A]" : "bg-[#00123A]/80"}`}
          >
            <div className="flex flex-col items-center gap-2">
              <Database size={32} />
              <span className="text-xl font-semibold">CRM</span>
              <span className="text-sm font-medium">Base de données</span>
            </div>
          </Button>

          <Button 
            onClick={() => setActiveTab("concours")}
            className={`flex-1 h-auto py-6 min-w-[200px] ${activeTab === "concours" ? "bg-[#00123A]" : "bg-[#00123A]/80"}`}
          >
            <div className="flex flex-col items-center gap-2">
              <Trophy size={32} />
              <span className="text-xl font-semibold">Concours</span>
              <span className="text-sm font-medium">#{counts.contest}</span>
            </div>
          </Button>

          <Button 
            onClick={() => setActiveTab("golf")}
            className={`flex-1 h-auto py-6 min-w-[200px] ${activeTab === "golf" ? "bg-[#00123A]" : "bg-[#00123A]/80"}`}
          >
            <div className="flex flex-col items-center gap-2">
              <Flag size={32} />
              <span className="text-xl font-semibold">Golf</span>
              <span className="text-sm font-medium">#{counts.golf}</span>
            </div>
          </Button>

          <Button
            onClick={() => setActiveTab("senat")}
            className={`flex-1 h-auto py-6 min-w-[200px] ${activeTab === "senat" ? "bg-[#00123A]" : "bg-[#00123A]/80"}`}
          >
            <div className="flex flex-col items-center gap-2">
              <Landmark size={32} />
              <span className="text-xl font-semibold">Dîner Sénat</span>
              <span className="text-sm font-medium">#{counts.senat}</span>
            </div>
          </Button>
        </div>

        {activeTab === "signataires" && <SignatoriesTab />}
        {activeTab === "membres" && <MembersTab />}
        {activeTab === "newsletter" && <NewsletterTab />}
        {activeTab === "crm" && <CRMTab />}
        {activeTab === "concours" && <ContestTab />}
        {activeTab === "golf" && <GolfTab />}
        {activeTab === "senat" && <SenatTab />}

        {!activeTab && (
          <div className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle>Bienvenue dans le tableau de bord</CardTitle>
                <CardDescription>Sélectionnez un onglet pour consulter les informations</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-center py-4 text-muted-foreground">
                  Veuillez cliquer sur l'un des boutons ci-dessus pour afficher les données
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
